import type { AgentReport } from '@ralph/contracts'
import { pluralCategory } from '@/i18n/format.ts'
import type { Counting } from './systems.ts'

/**
 * `admin.reports` — copy for the report triage queue (PRD-18 AGM-123).
 *
 * Two properties of the reporting channel are stated on the surface rather than left to
 * documentation, because an administrator working the queue is the person who has to hold
 * them: volume never suspends anything (suspension is a decision someone makes, on this
 * screen's sibling), and the reporter's identity stops here — it is visible to
 * administrators and never reaches the agent's owner.
 *
 * The per-reporter count is the false-report signal. It is a count, not a verdict: the same
 * number describes someone gaming the queue and someone who found five genuinely broken
 * agents in a week.
 *
 * ── Why these five categories are worded twice in this repository ──
 *
 * The consumer's report form and this queue carry the SAME five enum values with different
 * words, deliberately (PRD-27 D-9 amended): first-person sentences for somebody reporting an
 * agent, a moderation taxonomy for the administrator triaging it. Nothing here should be
 * folded into one shared map — a translator handed one map has no way to know the two
 * registers were a decision and will "fix" the inconsistency.
 */

export const reports = {
  title: 'Reported agents',
  lead: 'Reports from people using the platform. Reporter identity is visible here and never to the agent’s owner. Volume alone never changes a listing’s state.',
  loading: 'Loading reports…',
  unavailable: 'The report queue is not available on this deployment yet',
  unavailableBody: 'GET /api/admin/marketplace/reports has not answered.',
  empty: 'Nothing reported',
  emptyBody: 'Reports from consumers arrive here for triage.',

  /**
   * Both maps are typed against the wire shape rather than left as bare literals, so a
   * category or a status added to the contract is a compile error here instead of an
   * `undefined` in a badge (PRD-27 D-9).
   */
  categories: {
    impersonation: 'Impersonation',
    harmful: 'Harmful or unsafe',
    wrong: 'Inaccurate',
    inappropriate: 'Inappropriate',
    other: 'Other'
  } satisfies Record<AgentReport['category'], string>,

  statuses: {
    open: 'Open',
    triaged: 'Triaged',
    dismissed: 'Dismissed'
  } satisfies Record<AgentReport['status'], string>,

  reportedBy: (name: string): string => `reported by ${name}`,
  /**
   * Said with both forms rather than one, and not because the singular shows today — the row
   * renders this only above one. It is `Intl.PluralRules` because the count is a number in a
   * sentence, and a sentence that hardcodes the plural is a sentence that cannot be
   * translated: Portuguese takes the singular at zero as well as at one.
   */
  repeatReporter: (fmt: Counting, count: number): string =>
    pluralCategory(fmt.locale, count) === 'one'
      ? `${fmt.formatCount(count)} report from this person in the queue`
      : `${fmt.formatCount(count)} reports from this person in the queue`,
  openConversation: 'Open the flagged conversation',
  noConversation: 'No conversation was attached.',
  openListing: 'Open the listing',
  noteEmpty: 'No note was written.',

  triage: 'Mark triaged',
  dismiss: 'Dismiss',
  working: 'Recording…',
  failed: 'That decision was not recorded.',
  filterLabel: 'Show',
  filterOpen: 'Open only',
  filterAll: 'Everything'
}
